import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Dialog, DialogContent, DialogActions } from 'src/ui/dialog';
import { Button } from 'src/ui/button';
import { Input } from 'src/ui/input';
import { Label } from 'src/ui/label';
import { TextArea } from 'src/ui/text-area';
import { PhoneInput } from 'src/ui/phone-input';
import { Typography } from 'src/ui/typography';
import { trpc } from '../../../../../../utils/trpc';
import { zodResolver } from '@hookform/resolvers/zod';
import { getQueryKey } from '@trpc/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { inferProcedureOutput } from '@trpc/server';
import * as z from 'zod';
import type { AppRouter } from '../../../../../../server/trpc/router';
import { window_open } from 'src/utils/flutter';
import toast from 'react-hot-toast';
import { useApp } from 'src/@carketa/app/AppContext';
import { OrganizationSettings } from 'src/types/organizations/organizations';
import { useAnalytics } from 'use-analytics';

const schema = z.object({
  first_name: z.string().min(2, 'First Name must be at least 2 characters'),
  last_name: z.string().min(2, 'Last Name must be at least 2 characters'),
  email: z.string().email().min(2, 'Email must be at least 2 characters'),
  phone: z.string().min(10, 'Phone must be at least 10 characters'),
  comments: z.string(),
});

export type Vehicle = inferProcedureOutput<AppRouter['vehicles']['getById']>;
export type VehicleStickerOrId = inferProcedureOutput<AppRouter['vehicles']['getByStickerOrId']>;

interface Props {
  vehicle?: Vehicle | VehicleStickerOrId | undefined | null;
  open: boolean;
  handleClose: () => void;
}

export const CheckAvailabilityDialog: React.FC<Props> = ({ vehicle, open = false, handleClose }) => {
  const { track } = useAnalytics();
  const { me } = useApp();
  const [vehicleId, setVehicleId] = useState<string | undefined | null>('');

  useEffect(() => {
    setVehicleId(vehicle?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const queryClient = useQueryClient();
  const key = getQueryKey(trpc.vehicles.getById, { vehicle_id: vehicleId || '' }, 'query');

  const createLeadMutation = trpc.leads.createVehicleLead.useMutation({
    onError(error) {
      toast.error(error.message);
      console.error(error);
    },
    onSuccess() {
      toast.success('Details successfully saved!');

      track(
        'contact-dealer-submit',
        {
          ...getValues(),
          vehicle_id: vehicleId || '',
          salesAndMrktngEmails: (me?.organization?.settings as OrganizationSettings).emailAddresses,
        },
        {
          component: 'sales-packet',
        }
      );

      sendLeadtoDealership.mutate({
        ...getValues(),
        vehicle_id: vehicleId || '',
        salesAndMrktngEmails: (me?.organization?.settings as OrganizationSettings).emailAddresses,
      });

      console.log('success');
      queryClient.invalidateQueries({ queryKey: key });
      reset();

      handleClose();
    },
  });

  const sendLeadtoDealership = trpc.leads.sendLeadADFEmail.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      console.log('success');
    },
  });

  const defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    comments: '',
  };

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    comments: string;
  }) => {
    const payload = {
      vehicle_id: vehicle?.id || '',
      ...data,
      lead_source: 'sales_packet',
      salesperson: '',
      notify: false,
    };

    createLeadMutation.mutate(payload);
  };

  const onHyperlinkClick = (url: string) => {
    window_open(url, '_blank', 'noreferrer');
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent title="Check Availability" closeIcon>
        <Typography variant="body2" className="mb-6 text-xs">
          Request information for this vehicle. Fill out this form and this dealership&apos;s team will reach out to
          you.
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6 flex items-center">
            <div className="w-full space-y-2">
              <Controller
                name="first_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={value}
                      onChange={onChange}
                      placeholder="John"
                      className={errors.first_name ? 'border-destructive' : ''}
                    />
                  </>
                )}
              />
              {errors?.first_name && <p className="text-sm text-destructive">{errors.first_name.message || ''}</p>}
            </div>
          </div>
          <div className="mb-6 flex items-center">
            <div className="w-full space-y-2">
              <Controller
                name="last_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={value}
                      onChange={onChange}
                      placeholder="Doe"
                      className={errors.last_name ? 'border-destructive' : ''}
                    />
                  </>
                )}
              />
              {errors?.last_name && <p className="text-sm text-destructive">{errors.last_name.message || ''}</p>}
            </div>
          </div>

          <div className="mb-6 flex items-center gap-4">
            <div className="w-full space-y-2">
              <Controller
                name="email"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={value}
                      onChange={onChange}
                      placeholder="johndoe@email.com"
                      className={errors.email ? 'border-destructive' : ''}
                    />
                  </>
                )}
              />
              {errors?.email && <p className="text-sm text-destructive">{errors.email.message || ''}</p>}
            </div>
          </div>
          <div className="mb-6 flex items-center gap-4">
            <div className="w-full space-y-2">
              <Controller
                name="phone"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Label htmlFor="phone">Phone</Label>
                    <PhoneInput
                      id="phone"
                      value={value}
                      onChange={onChange}
                      className={errors.phone ? 'border-destructive' : ''}
                    />
                  </>
                )}
              />
              {errors?.phone && <p className="text-sm text-destructive">{errors.phone.message || ''}</p>}
            </div>
          </div>
          <div className="mb-6 flex items-center gap-4">
            <div className="w-full space-y-2">
              <Controller
                name="comments"
                control={control}
                rules={{ required: false }}
                render={({ field: { value, onChange } }) => (
                  <TextArea
                    id="comments"
                    value={value}
                    onChange={onChange}
                    label="Comments(Optional)"
                    placeholder="Comments"
                    error={Boolean(errors.comments)}
                    minRows={4}
                    fullWidth
                  />
                )}
              />
              {errors?.comments && <p className="text-sm text-destructive">{errors.comments.message || ''}</p>}
            </div>
          </div>

          <DialogActions>
            <Button type="submit" variant="contained">
              Contact Dealer
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                track(
                  'check-availability-cancel-click',
                  {},
                  {
                    component: 'sales-packet',
                  }
                );
                handleClose();
                reset();
              }}
            >
              Cancel
            </Button>
          </DialogActions>
          <div className="mb-6 mt-6 flex items-center">
            <Typography variant="body2" className="text-xs italic">
              By submitting my contact information through Carketa, I agree to receive communications from Carketa, from
              the vehicle&apos;s seller, and from the seller&apos;s agent(s). If I include my phone number, I agree to
              receive calls and text messages (including via automation). I can opt out at any time. I also agree to the{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onHyperlinkClick('https://carketa.com/terms-and-conditions/');
                }}
                className="cursor-pointer text-primary hover:underline"
              >
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onHyperlinkClick('https://carketa.com/privacy-policy/');
                }}
                className="cursor-pointer text-primary hover:underline"
              >
                Privacy Policy
              </a>
              , which explains how my data is used to better understand my vehicle shopping interests. This consent is
              not required as a condition of purchase. Standard message and data rates may apply.
            </Typography>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
